import * as EventTracker from '@src/analytics/EventTracker';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { VideoStatus } from '@src/types.ts/video';
import { extractErrorInfo } from '@src/utility/error';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { LogManager } from '@src/utility/Logger';
import { uniqBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import CheckIcon from '../../svgs/CheckIcon';
import EarthIcon from '../../svgs/EarthIcon';
import LinkIcon from '../../svgs/LinkIcon';
import ShareIcon from '../../svgs/ShareIcon';
import { handleCopyToClipboard } from '../../utility/general';
import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '../common/dialogStyles';
import Input from '../common/Input';
import Modal from '../common/Modal';
import Switch from '../common/Switch';
import { buildClipLiveLink } from './clipLink';

import type { AssociatedVideo, VideoClip } from '../../types.ts/story';

const logger = LogManager.getLogger('component:ClipShareUnlistedLinkModal');

type Props = {
  clip: VideoClip | AssociatedVideo;
  mainClip?: VideoClip;
  dimension: string | null;
  /**
   * PLAT-11485: BCP-47 language of the publish record being shared, or null/
   * undefined for the story's original language (see `shareLanguageForRecord`).
   * Pins the link to that language's render instead of the original.
   */
  language?: string | null;
  onClose: () => void;
  /** Override the Modal backdrop z-index (e.g. to paint above a high-z drawer). */
  backdropZIndex?: number;
};

const ClipShareUnlistedLinkModal = (props: Props) => {
  const { datoClientStore } = useStore();
  const videoCreator = useVideoCreatorStore();
  const { clip, mainClip, dimension, language, onClose, backdropZIndex } =
    props;

  // Download permission is a per-clip-family concept, not per aspect ratio:
  // the toggle fans out to the main clip and every variant. When the modal is
  // opened on a non-16:9 variant, `clip` is an `AssociatedVideo` that lacks
  // `isDownloadEnabled`/`_allReferencingVideos` and may not carry a populated
  // `associatedVideos`, so always anchor the download state on `mainClip` when
  // it's available (it's the `VideoClip` with the full relationships).
  const downloadClip = mainClip ?? clip;

  const [downloadsEnabled, setDownloadsEnabled] = useState(() => {
    if ('isDownloadEnabled' in downloadClip)
      return !!downloadClip.isDownloadEnabled;
    return false;
  });
  const [downloadsEnabledLoading, setDownloadsEnabledLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const videoId = ('id' in clip && clip.id) || mainClip?.id;
  const link = useMemo(
    () => buildClipLiveLink(videoId || undefined, dimension, language),
    [videoId, dimension, language],
  );
  const hasValidLink = !!link;

  // The button's check-icon swap is the inline affordance; the toast carries
  // the confirmation the icon-only button can only state in its aria-label.
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 3000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopyLink = async () => {
    await handleCopyToClipboard(link);
    EventTracker.trackClipLinkCopy({
      clip_id: clip.id,
      story_id: videoCreator.story?.id,
      action: 'copy',
      link_type: 'unlisted',
    });
    setCopied(true);
    videoCreator.showToast({ state: 'success', message: 'Link copied' });
  };

  const handleToggleDownloads = async () => {
    const current = downloadsEnabled;
    try {
      setDownloadsEnabledLoading(true);
      setDownloadsEnabled(!current);
      const referencingVideos =
        ('_allReferencingVideos' in downloadClip
          ? downloadClip._allReferencingVideos
          : undefined) || [];
      const relatedClips = uniqBy(
        [
          ...(downloadClip.associatedVideos || []),
          ...referencingVideos,
          ...(referencingVideos.flatMap(v => v.associatedVideos) || []),
        ],
        v => v.id,
      ).filter(
        v => v.videoStatus === VideoStatus.Rendered && v.id !== downloadClip.id,
      );
      const requests = [
        datoClientStore.videoRepository?.saveOrUpdateVideo({
          id: downloadClip.id,
          isDownloadEnabled: !downloadsEnabled,
        }),
        ...relatedClips.map(video =>
          datoClientStore.videoRepository?.saveOrUpdateVideo({
            id: video.id,
            isDownloadEnabled: !downloadsEnabled,
          }),
        ),
      ].filter(Boolean);
      await Promise.all(requests);
    } catch (error) {
      logger.error('Error toggling video downloads', extractErrorInfo(error));
      setDownloadsEnabled(current);
    } finally {
      setDownloadsEnabledLoading(false);
    }
  };

  return (
    <Modal isOpen closeModal={onClose} backdropZIndex={backdropZIndex}>
      <DialogCard>
        <DialogHeader>
          <DialogTitle>Share Live Link</DialogTitle>
        </DialogHeader>
        {hasValidLink ? (
          <Body>
            <LinkRow>
              <Input
                fullWidth
                readOnly
                value={link}
                onFocus={e => e.currentTarget.select()}
              />
              <CopyLinkButton
                onClick={handleCopyLink}
                disabled={copied}
                title={copied ? 'Copied!' : 'Copy Link'}
                aria-label={copied ? 'Copied!' : 'Copy Link'}
              >
                {copied ? (
                  <CheckIcon fillColor="currentColor" width="14" height="14" />
                ) : (
                  <LinkIcon strokeColor="currentColor" width="16" />
                )}
              </CopyLinkButton>
            </LinkRow>
            <LinkNote>
              <EarthIcon />
              {/* The language is named only for translated links: the Downloads
                  row it was opened from shows a language per row, so without it
                  the modal gives no confirmation of which variant is being
                  shared. Original-language links keep the original copy.

                  The recency line is unconditional because it is true of both:
                  the link carries a language, never a publish record, so it
                  resolves to that language's most recent publish. It matters
                  most when the modal was opened from an older Downloads row —
                  Download beside it hands back that exact render, while this
                  link does not — but a canonical publish likewise overwrites
                  `videoFilePrimary`, so the original-language link has always
                  behaved this way too. */}
              <span>
                {language
                  ? `Anyone with this link can view the ${formatLanguageCaption(
                      language,
                    )} version.`
                  : 'Anyone with this link can view the video.'}{' '}
                It always plays the most recent publish.
              </span>
            </LinkNote>
            <Switch
              small
              label="Allow viewers to download"
              checked={downloadsEnabled}
              disabled={downloadsEnabledLoading}
              onClick={handleToggleDownloads}
            />
            <DialogFooter>
              <OpenLinkButton
                as="a"
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  EventTracker.trackClipLinkCopy({
                    clip_id: clip.id,
                    story_id: videoCreator.story?.id,
                    action: 'open',
                    link_type: 'unlisted',
                  })
                }
              >
                <ShareIcon strokeColor="currentColor" width="16" height="16" />
                Open Link
              </OpenLinkButton>
            </DialogFooter>
          </Body>
        ) : (
          <LinkNote>
            <EarthIcon />
            <span>
              Link is not available yet. Please wait for the video to finish
              saving and try again.
            </span>
          </LinkNote>
        )}
      </DialogCard>
    </Modal>
  );
};

export default ClipShareUnlistedLinkModal;

// Tighter rhythm than the DialogCard's default lg gap — the link row, its
// access note, and the download switch read as one compact group.
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const LinkRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: var(--spacing-sm);
  min-width: 0;
`;

// Icon-only square inline with the link field (label lives in the tooltip /
// aria-label); the icon swaps to a check while the copied state is active.
const CopyLinkButton = styled(PrimaryButton)`
  flex-shrink: 0;
  height: auto;
  width: var(--btn-h-lg);
  padding: 0;
`;

const LinkNote = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--text-muted);
  font-size: var(--text-xs);

  svg {
    flex-shrink: 0;
  }
`;

const OpenLinkButton = styled(SecondaryButton)`
  text-decoration: none;
`;
